import * as React from 'react';
import { Select } from 'antd';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/shadcn/components/ui/sheet';
import { Button } from '@/shadcn/components/ui/button';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { OFFER_STATUS_TYPE, STATS_OFFER_TYPE } from '../../types/globalTypes';
import { Label } from '@/shadcn/components/ui/label';

interface OfferFiltersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFilters: OFFER_STATUS_TYPE[];
  setSelectedFilters: (filters: OFFER_STATUS_TYPE[]) => void;
  archiveOffer: boolean;
  selectedOfferType: STATS_OFFER_TYPE;
  setSelectedOfferType: (type: STATS_OFFER_TYPE) => void;
  programs: { id: number; title: string }[];
  selectedPrograms: number[];
  setSelectedPrograms: (ids: number[]) => void;
}

export function RefreshOfferFiltersDrawer({
  open,
  onOpenChange,
  selectedFilters,
  setSelectedFilters,
  archiveOffer,
  selectedOfferType,
  setSelectedOfferType,
  programs = [],
  selectedPrograms,
  setSelectedPrograms,
}: OfferFiltersDrawerProps) {
  const [tempSelectedFilters, setTempSelectedFilters] = React.useState<OFFER_STATUS_TYPE[]>(selectedFilters);
  const [tempOfferType, setTempOfferType] = React.useState<STATS_OFFER_TYPE>(selectedOfferType);
  const [tempSelectedPrograms, setTempSelectedPrograms] = React.useState<number[]>(selectedPrograms || []);

  React.useEffect(() => {
    setTempSelectedFilters(selectedFilters);
  }, [selectedFilters, open]);
  React.useEffect(() => {
    setTempOfferType(selectedOfferType);
  }, [selectedOfferType, open]);
  React.useEffect(() => {
    setTempSelectedPrograms(selectedPrograms || []);
  }, [selectedPrograms, open]);

  const filterOptions = [
    { label: 'Active', value: OFFER_STATUS_TYPE.ACTIVE },
    { label: 'Paused', value: OFFER_STATUS_TYPE.PAUSED },
    { label: 'Expired', value: OFFER_STATUS_TYPE.EXPIRED },
    ...(archiveOffer ? [{ label: 'Archived', value: OFFER_STATUS_TYPE.ARCHIVED }] : []),
  ];

  function handleApply() {
    // If neither offer type is checked, set to ALL_OFFERS
    const isSalesChecked = tempOfferType === STATS_OFFER_TYPE.LINK_OFFER;
    const isPromoChecked = tempOfferType === STATS_OFFER_TYPE.PROMO_OFFER;
    let offerTypeToSet = tempOfferType;
    if (!isSalesChecked && !isPromoChecked) {
      offerTypeToSet = STATS_OFFER_TYPE.ALL_OFFERS;
    }
    setSelectedFilters(tempSelectedFilters);
    setSelectedOfferType(offerTypeToSet);
    setSelectedPrograms(tempSelectedPrograms);
    onOpenChange(false);
  }

  function handleCancel() {
    setTempSelectedFilters(selectedFilters);
    setTempOfferType(selectedOfferType);
    setTempSelectedPrograms(selectedPrograms || []);
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col h-full bg-background p-0 gap-0">
        <SheetHeader className="p-4">
          <SheetTitle className="m-0">Filters</SheetTitle>
        </SheetHeader>
        <div className="border-b" />
        <div className="flex-1 px-4 flex flex-col gap-6 py-4">
          <div className="flex flex-col gap-4">
            <Label>Offer Status</Label>
            <div className="flex flex-col gap-2">
              {filterOptions.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2">
                  <Checkbox
                    checked={tempSelectedFilters.includes(opt.value)}
                    onCheckedChange={(checked) => {
                      setTempSelectedFilters(checked
                        ? [...tempSelectedFilters, opt.value]
                        : tempSelectedFilters.filter((v) => v !== opt.value)
                      );
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <Label>Offer Type</Label>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={tempOfferType === STATS_OFFER_TYPE.LINK_OFFER}
                  onCheckedChange={(checked) =>
                    setTempOfferType(checked ? STATS_OFFER_TYPE.LINK_OFFER : STATS_OFFER_TYPE.ALL_OFFERS)}
                />
                <span>Sales Link Offer</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={tempOfferType === STATS_OFFER_TYPE.PROMO_OFFER}
                  onCheckedChange={(checked) =>
                    setTempOfferType(checked ? STATS_OFFER_TYPE.PROMO_OFFER : STATS_OFFER_TYPE.ALL_OFFERS)}
                />
                <span>Promo Code Offer</span>
              </label>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <Label>Connected Projects</Label>
            <Select
              mode="multiple"
              showSearch
              placeholder="Select Projects"
              value={tempSelectedPrograms}
              onChange={setTempSelectedPrograms}
              className="w-full"
              options={programs.map((p) => ({ value: p.id, label: p.title }))}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              dropdownStyle={{ minHeight: 240, overflowY: 'auto' }}
              filterOption={(input, option) =>
                String(option?.label).toLowerCase().includes(input.toLowerCase())}
            />
          </div>
        </div>
        <div className="border-t" />
        <SheetFooter className="justify-start sm:justify-start p-4">
          <Button onClick={handleApply}>Apply</Button>
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
